/**
 * TimelineBars Component
 * Renders period bars with lane positioning and rounded corners
 * Enhanced with path memoization and error handling
 */

import React, { useMemo } from 'react';
import type { PeriodWithLane, TimelineViewport, TruckTimelineData } from '../types';
import { dateToPosition } from '../utils';
import { LANE_HEIGHT, LANE_SPACING, MIN_BAR_WIDTH, CORNER_RADIUS } from '../constants';
import { memoizePath } from '../utils/performance';
import { isValidNumber, safeDimension } from '../utils/validation';

interface TimelineBarsProps {
  periods: PeriodWithLane[];
  viewport: TimelineViewport;
  innerWidth: number;
  currentY: number;
  activeGroupIds: Set<string>;
  truckData: TruckTimelineData;
  onBarHover: (e: React.MouseEvent, truckData: TruckTimelineData) => void;
  onBarLeave: () => void;
  padding?: { top: number; right: number; bottom: number; left: number };
  deviceType?: 'desktop' | 'tablet' | 'mobile';
}

export const TimelineBars: React.FC<TimelineBarsProps> = ({
  periods,
  viewport,
  innerWidth,
  currentY,
  activeGroupIds,
  truckData,
  onBarHover,
  onBarLeave,
  padding = { top: 20, right: 20, bottom: 70, left: 180 },
  deviceType = 'desktop',
}) => {
  const laneHeight = LANE_HEIGHT[deviceType];
  // Memoize path generator function
  const generatePath = useMemo(() => {
    return (x: number, y: number, w: number, h: number, r: number, position?: string): string => {
      // Performance: Memoize SVG paths with cache key
      const cacheKey = `${x}-${y}-${w}-${h}-${r}-${position || 'middle'}`;

      return memoizePath(cacheKey, () => {
        if (position === 'start') {
          return `M ${x + r},${y} L ${x + w},${y} L ${x + w},${y + h} L ${x + r},${y + h} Q ${x},${y + h} ${x},${y + h - r} L ${x},${y + r} Q ${x},${y} ${x + r},${y} Z`;
        } else if (position === 'end') {
          return `M ${x},${y} L ${x + w - r},${y} Q ${x + w},${y} ${x + w},${y + r} L ${x + w},${y + h - r} Q ${x + w},${y + h} ${x + w - r},${y + h} L ${x},${y + h} Z`;
        } else {
          return `M ${x},${y} L ${x + w},${y} L ${x + w},${y + h} L ${x},${y + h} Z`;
        }
      });
    };
  }, []);

  return (
    <g clipPath="url(#chart-clip)">
      {periods.map((period) => {
        try {
          // Validate period dates
          if (!period.startDate || !period.endDate) return null;

          // Skip if period is completely outside viewport
          if (period.endDate < viewport.startDate || period.startDate > viewport.endDate) {
            return null;
          }

          // Calculate raw positions with validation
          const startX = dateToPosition(period.startDate, viewport, innerWidth);
          const endX = dateToPosition(period.endDate, viewport, innerWidth);

          if (!isValidNumber(startX) || !isValidNumber(endX)) return null;

          // Clamp to visible area (0 to innerWidth)
          const clampedStartX = Math.max(0, Math.min(innerWidth, startX));
          const clampedEndX = Math.max(0, Math.min(innerWidth, endX));

          // Calculate clamped width with safe fallback
          const barWidth = safeDimension(
            clampedEndX - clampedStartX,
            MIN_BAR_WIDTH,
            MIN_BAR_WIDTH
          );

          // Skip if bar has no visible width
          if (barWidth <= 0) return null;

          // Calculate Y position based on lane with validation
          const lane = safeDimension(period.lane, 0, 0);
          const laneY = safeDimension(
            currentY + lane * (laneHeight + LANE_SPACING) + LANE_SPACING,
            currentY,
            0
          );
          const barHeight = safeDimension(laneHeight - LANE_SPACING, 20, 1);

          // Create SVG path for partial border radius
          const x = padding.left + clampedStartX;
          const y = laneY;
          const w = barWidth;
          const h = barHeight;
          const r = CORNER_RADIUS;

          // Performance: Use memoized path generation
          const pathData = generatePath(x, y, w, h, r, period.segmentPosition);

          // Check if this bar's entire group (89-day bar) is active
          const isBarGroupActive = period.groupId && activeGroupIds.has(period.groupId);

          return (
            <g key={period.id}>
              {/* Original colored bar */}
              <path
                d={pathData}
                fill={period.color || '#6b7280'}
                opacity={0.9}
                onMouseEnter={(e) => onBarHover(e, truckData)}
                onMouseLeave={onBarLeave}
                style={{ cursor: 'pointer' }}
              />
              {/* 20% black overlay - removed when red line intersects any part of the 89-day bar */}
              {!isBarGroupActive && (
                <path d={pathData} fill="black" opacity={0.2} pointerEvents="none" />
              )}
            </g>
          );
        } catch (error) {
          console.error('Error rendering timeline bar:', error, period);
          return null;
        }
      })}
    </g>
  );
};
